/**
 * Imagem.
 *
 * O endereço vem de `props.url` quando é fixo, ou do dado da tela quando vem
 * junto com o registro — que é o caso comum: foto do produto, logo do
 * marketplace, comprovante anexado.
 *
 * **A ampliação usa o `NzImageService`.** O canvas antigo montava o próprio
 * lightbox com `document.createElement` e `cssText` com `background: white`, e
 * injetava um `<style>` no `document.head` **a cada abertura** — um `<style>`
 * vazado por clique, e branco fixo no tema escuro. O NG-Zorro já tem preview
 * pronto, com tema, foco por teclado e tradução.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { NzImageModule, NzImageService } from 'ng-zorro-antd/image';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TranslateService } from '@ngx-translate/core';

import { ScreenNode } from '../../model/node-props';
import { translateTAValue } from '../../../system-utils/pipes/translateTA';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { ScreenContext } from '../screen-context';
import { resolvePath } from '../../runtime/data-binding';

@Component({
  selector: 'tem-image-field',
  standalone: true,
  imports: [CommonModule, NzImageModule, NzIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (source(); as url) {
      <img
        class="tem-image__img"
        [src]="url"
        [alt]="altText()"
        [title]="tooltip()"
        [style.width.px]="width()"
        [style.height.px]="height()"
        [class.tem-image__img--zoomable]="canZoom()"
        (click)="zoom(url)" />
    } @else {
      <!-- Sem endereço, o espaço fica MARCADO em vez de invisível: uma imagem
           que não carregou e um espaço vazio são indistinguíveis, e o usuário
           não tem como saber que ali deveria haver algo. -->
      <div class="tem-image__empty" [style.width.px]="width()" [style.height.px]="height()">
        <nz-icon nzType="picture" nzTheme="outline" />
      </div>
    }
  `,
  styles: [`
    :host { display: block; }

    .tem-image__img {
      max-width: 100%;
      object-fit: contain;
      border-radius: var(--tem-radius);
    }

    .tem-image__img--zoomable { cursor: zoom-in; }

    .tem-image__empty {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 64px;
      min-height: 64px;
      border: 1px dashed var(--tem-border);
      border-radius: var(--tem-radius);
      color: var(--tem-muted);
      font-size: 24px;
    }
  `]
})
export class ImageFieldRenderer implements NodeRenderer<'image'> {

  readonly node = input.required<ScreenNode<'image'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  private readonly images = inject(NzImageService);
  private readonly translate = inject(TranslateService);
  private readonly context = inject(ScreenContext, { optional: true });

  /**
   * O endereço da imagem.
   *
   * A URL fixa das props vence quando existe — é uma escolha deliberada de quem
   * montou a tela. Sem ela, procura no dado, pelo caminho declarado.
   */
  readonly source = computed<string>(() => {
    const fixa = this.node().props.url;

    if (typeof fixa === 'string' && fixa.trim().length > 0) {
      return fixa;
    }

    const caminho = this.node().binding?.dataPath;

    if (caminho === undefined || this.context === null) {
      return '';
    }

    const valor = resolvePath(this.context.data(), caminho);

    return typeof valor === 'string' ? valor : '';
  });

  readonly altText = computed<string>(
    () => translateTAValue(this.translate, this.node().props.altKey ?? '')
  );

  readonly tooltip = computed<string>(
    () => translateTAValue(this.translate, this.node().props.tooltipKey ?? '')
  );

  readonly width = computed<number | null>(() => this.node().props.width ?? null);
  readonly height = computed<number | null>(() => this.node().props.height ?? null);

  /**
   * Ampliar não vale no canvas.
   *
   * Clicar numa imagem do editor deve selecionar o nó, não abrir um visualizador
   * em tela cheia por cima do que se está montando.
   */
  readonly canZoom = computed<boolean>(
    () => this.node().props.enableLightbox === true && this.mode() !== 'editPreview'
  );

  zoom(url: string): void {
    if (!this.canZoom()) {
      return;
    }

    this.images.preview([{ src: url, alt: this.altText() }]);
  }
}
